import { buildingDef } from '../content/buildings';
import { TILE } from '../sim/constants';
import { EventKind } from '../sim/types';
import type { GameState } from '../sim/types';

/*
 * "הבסיס שלך מותקף".
 *
 * המפה היא 96×96 אריחים והמסך מראה חלק קטן ממנה. בלי ההתראה הזו השחקן מגלה
 * שתקפו אותו רק כשהוא במקרה גולל לשם — כלומר אחרי שהמפעל כבר נהרס.
 *
 * **תצוגה בלבד.** המודול קורא מהמצב, לא כותב אליו, ואינו משפיע על שום החלטה
 * במשחק. אין כאן שעון: הזמן נכנס דרך `deltaMs` שהקורא מודד, ולכן אפשר לבדוק
 * צינון ותפוגה בטסט בלי להזיז שעון אמיתי.
 *
 * איך מזהים נזק — שתי דרכים, כי אף אחת מהן לבדה אינה שלמה:
 *
 *   1. **ירידת hp** של מבנה או יחידה שלי לעומת מה שנמדד בטיק הקודם. זה תופס
 *      כל פגיעה, מכל מקור.
 *   2. **אירוע מוות** (`EventKind.Death`) שה-owner שלו הוא השחקן המקומי.
 *      ישות שנהרסה **נעלמת מהמצב**, ולכן אין ממי להשוות hp — בלי האירוע,
 *      דווקא ההרס המוחלט היה עובר בשקט.
 *
 * ולמה `lastTick`: הקורא מפעיל את זה 60 פעם בשנייה, והסימולציה מתקדמת 20. אותם
 * `state.events` בדיוק מופיעים בשלושה פריימים רצופים. בלי לזכור איזה טיק כבר
 * נבדק, אירוע מוות אחד היה נספר שלוש פעמים.
 */

/** לא מתריעים יותר מפעם ב-8 שניות. קרב מתמשך אינו 400 התראות. */
export const ALERT_COOLDOWN_MS = 8000;

/** כמה זמן ההתראה "דלוקה" (מהבהבת). קצר מהצינון, בכוונה. */
export const ALERT_DURATION_MS = 5000;

export interface AlertState {
    /**
     * ה-hp האחרון שנמדד לכל ישות של השחקן המקומי.
     * יחידות ומבנים חולקים מרחב מזהים אחד (`nextEntityId`), ולכן מפה אחת מספיקה.
     * נבנית מחדש בכל טיק — כך ישות שמתה לא נשארת בזיכרון לנצח.
     */
    hp: Map<number, number>;
    /** הטיק שכבר נבדק. -1 = טרם נבדק דבר. */
    lastTick: number;
    /** מילישניות שנותרו עד שמותר להתריע שוב. */
    cooldownMs: number;
    /** מילישניות שנותרו לתצוגת ההתראה. */
    remainingMs: number;
    /** האם הייתה אי פעם התראה. false = אין מה להחזיר. */
    fired: boolean;
    worldX: number;
    worldY: number;
}

export interface AlertReading {
    /** דלוקה כרגע — הזמן להבהב ולהשמיע. */
    readonly active: boolean;
    /** איפה זה קרה. תקף גם כשההתראה כבה, כדי ש"קפוץ לשם" ימשיך לעבוד. */
    readonly worldX: number;
    readonly worldY: number;
}

export function createAlerts(): AlertState {
    return {
        hp: new Map(),
        lastTick: -1,
        cooldownMs: 0,
        remainingMs: 0,
        fired: false,
        worldX: 0,
        worldY: 0,
    };
}

/**
 * מקדם את הטיימרים, בודק את הטיק החדש (אם יש), ומחזיר את ההתראה הנוכחית.
 *
 * `null` = מעולם לא הותקפנו. אחרת מוחזר המיקום האחרון, עם `active` שאומר אם
 * ההתראה עוד דלוקה.
 */
export function updateAlerts(
    alerts: AlertState,
    state: GameState,
    localPlayer: number,
    deltaMs: number,
): AlertReading | null {
    if (deltaMs > 0) {
        alerts.cooldownMs = Math.max(0, alerts.cooldownMs - deltaMs);
        alerts.remainingMs = Math.max(0, alerts.remainingMs - deltaMs);
    }

    if (state.tick !== alerts.lastTick) {
        const hit = findDamage(alerts, state, localPlayer);
        alerts.lastTick = state.tick;
        if (hit && alerts.cooldownMs === 0) {
            alerts.fired = true;
            alerts.worldX = hit.x;
            alerts.worldY = hit.y;
            alerts.cooldownMs = ALERT_COOLDOWN_MS;
            alerts.remainingMs = ALERT_DURATION_MS;
        }
    }

    if (!alerts.fired) return null;
    return { active: alerts.remainingMs > 0, worldX: alerts.worldX, worldY: alerts.worldY };
}

/**
 * מוצא נזק שספג השחקן המקומי בטיק הזה, ומעדכן את מפת ה-hp.
 *
 * סדר העדיפות אינו שרירותי: **מבנה שנפגע הוא "הבסיס מותקף"**, וזה הדבר שהשחקן
 * חייב לדעת עליו. אחריו אובדן מוחלט של ישות, ורק בסוף יחידה שנשרטה בשדה.
 */
function findDamage(
    alerts: AlertState,
    state: GameState,
    localPlayer: number,
): { x: number; y: number } | null {
    const previous = alerts.hp;
    const next = new Map<number, number>();

    let building: { x: number; y: number } | null = null;
    let unit: { x: number; y: number } | null = null;

    for (const item of state.buildings) {
        if (item.owner !== localPlayer) continue;
        next.set(item.id, item.hp);
        const before = previous.get(item.id);
        // ישות חדשה אינה נזק: אין לה "לפני".
        if (before === undefined || item.hp >= before || building) continue;
        const def = buildingDef(item.type);
        building = {
            x: (item.tileX + def.w / 2) * TILE,
            y: (item.tileY + def.h / 2) * TILE,
        };
    }

    for (const item of state.units) {
        if (item.owner !== localPlayer) continue;
        next.set(item.id, item.hp);
        const before = previous.get(item.id);
        if (before === undefined || item.hp >= before || unit) continue;
        unit = { x: item.x, y: item.y };
    }

    alerts.hp = next;

    if (building) return building;

    for (const event of state.events) {
        if (event.kind === EventKind.Death && event.b === localPlayer) {
            return { x: event.x, y: event.y };
        }
    }

    return unit;
}
